import { useEffect, useState } from 'react';
import { api, usd } from '../lib/api';
import { ErrorNote, Modal } from './ui';

// Pick (or quickly create) the loyalty customer for the current sale.
export function CustomerPicker({ onClose, onPick }: { onClose: () => void; onPick: (c: any) => void }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [err, setErr] = useState<unknown>(null);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: '', phone: '', club: false });

  useEffect(() => {
    const t = setTimeout(() => { api<any[]>(`/api/customers?q=${encodeURIComponent(q)}`).then(setRows).catch(setErr); }, 200);
    return () => clearTimeout(t);
  }, [q]);

  async function create() {
    setErr(null);
    try { onPick(await api('/api/customers', { body: { name: f.name, phone: f.phone || null, club: f.club } })); } catch (e) { setErr(e); }
  }

  return (
    <Modal title={adding ? 'New customer' : 'Customer'} onClose={onClose} footer={adding
      ? <><button onClick={() => setAdding(false)}>Back</button><button className="primary" disabled={f.name.trim().length < 1} onClick={create}>Save and use</button></>
      : <button onClick={() => { setAdding(true); setF({ ...f, name: /\d/.test(q) ? '' : q, phone: /\d/.test(q) ? q : '' }); }}>New customer</button>}>
      <ErrorNote error={err} />
      {adding ? (
        <>
          <div className="field-row">
            <label>Name<input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
            <label>Phone<input inputMode="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></label>
          </div>
          <label className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)' }}>
            <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={f.club} onChange={(e) => setF({ ...f, club: e.target.checked })} /> Club member
          </label>
        </>
      ) : (
        <>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, phone or email" aria-label="Find customer" />
          <div className="table-wrap" style={{ maxHeight: 320, overflow: 'auto' }}>
            <table className="grid"><tbody>
              {rows.map((c) => (
                <tr key={c.id} className="clickable" onClick={() => onPick(c)}>
                  <td><div style={{ fontWeight: 600 }}>{c.name}</div><div className="faint">{c.phone ?? c.email ?? ''}</div></td>
                  <td>{c.club && <span className="chip good">Club</span>}</td>
                  <td className="n">{c.points} pts</td>
                </tr>))}
            </tbody></table>
            {rows.length === 0 && <div className="empty">No customers found.</div>}
          </div>
        </>
      )}
    </Modal>
  );
}

// Look up a gift card and use its balance toward this sale.
export function GiftCardLookup({ onClose, onApply }: { onClose: () => void; onApply: (card: { code: string; balance: number }) => void }) {
  const [code, setCode] = useState('');
  const [card, setCard] = useState<any>(null);
  const [err, setErr] = useState<unknown>(null);
  async function look(e: React.FormEvent) {
    e.preventDefault(); setErr(null); setCard(null);
    try { setCard(await api(`/api/gift-cards/${encodeURIComponent(code.trim())}`)); } catch (e2) { setErr(e2); }
  }
  return (
    <Modal title="Gift card" onClose={onClose} footer={<button className="primary" disabled={!card || !card.active || card.balance_cents <= 0} onClick={() => onApply({ code: card.code, balance: card.balance_cents })}>Use this card</button>}>
      <form className="scanbar" onSubmit={look}>
        <input autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="Scan or type the card number" aria-label="Gift card number" />
        <button className="primary">Check</button>
      </form>
      <ErrorNote error={err} />
      {card && (
        <div className={`notice ${card.active && card.balance_cents > 0 ? 'good' : 'bad'}`}>
          {card.active ? <>Balance <b>{usd(card.balance_cents)}</b></> : 'This card is switched off.'}
        </div>
      )}
    </Modal>
  );
}

// Recall a cart a floor worker built on a phone: type its 4-digit number or tap it in the list.
export function RecallCart({ onClose, onRecall }: { onClose: () => void; onRecall: (cart: { code: string; lines: { itemId: string; qty: number }[]; customer: any | null }) => void }) {
  const [list, setList] = useState<any[] | null>(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => { api<any[]>('/api/held-carts').then(setList).catch(setErr); }, []);
  async function recall(ref: string) {
    setErr(null);
    try { onRecall(await api(`/api/held-carts/${encodeURIComponent(ref)}/recall`, { body: {} })); } catch (e) { setErr(e); }
  }
  return (
    <Modal title="Recall a cart" onClose={onClose}>
      <form className="scanbar" onSubmit={(e) => { e.preventDefault(); if (code.trim()) recall(code.trim()); }}>
        <input autoFocus inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="Cart number" aria-label="Cart number" />
        <button className="primary big">Recall</button>
      </form>
      <ErrorNote error={err} />
      {list && list.length === 0 && <div className="empty">No carts are waiting.</div>}
      {list?.map((h) => (
        <button key={h.id} className="ghost" style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }} onClick={() => recall(h.id)}>
          <span>#{h.code} · {h.lines.length} items{h.customer_name ? ` · ${h.customer_name}` : ''}</span><b>{usd(h.total_cents)}</b>
        </button>))}
    </Modal>
  );
}

// Lottery cash at the register: instant and online lottery sales and payouts. Lottery money is owed to the state, so it is kept apart from store sales.
const LOTTERY_KINDS: [string, string][] = [['instant_sale', 'Instant sale'], ['online_sale', 'Online sale'], ['instant_payout', 'Instant payout'], ['online_payout', 'Online payout']];

export function LotteryQuick({ onClose }: { onClose: () => void }) {
  const [kind, setKind] = useState('instant_sale');
  const [amount, setAmount] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const [today, setToday] = useState<any[]>([]);
  const cents = Math.round(Number(amount || '0') * 100);
  const reload = () => api<any[]>('/api/lottery/txns').then(setToday).catch(() => {});
  useEffect(() => { reload(); }, []);
  const total = (ks: string[]) => today.filter((t) => !t.voided && ks.includes(t.kind)).reduce((s, t) => s + t.amount_cents, 0);
  async function save() {
    setErr(null);
    try { await api('/api/lottery/txns', { body: { kind, amountCents: cents } }); setAmount(''); reload(); } catch (e) { setErr(e); }
  }
  return (
    <Modal title="Lottery" onClose={onClose} footer={<button className="primary big" disabled={!(cents > 0)} onClick={save}>Record {cents > 0 ? usd(cents) : ''}</button>}>
      <ErrorNote error={err} />
      <div className="two" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {LOTTERY_KINDS.map(([k, label]) => <button key={k} className={kind === k ? 'primary' : ''} onClick={() => setKind(k)}>{label}</button>)}
      </div>
      <label>Amount<input autoFocus className="num-input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} placeholder="0.00" /></label>
      <div className="faint">Today: sales {usd(total(['instant_sale', 'online_sale']))} · paid out {usd(total(['instant_payout', 'online_payout']))}</div>
      {today.slice(0, 5).map((t) => (
        <div key={t.id} className="m-line" style={t.voided ? { opacity: 0.4 } : undefined}>
          <div className="grow">{LOTTERY_KINDS.find((x) => x[0] === t.kind)?.[1]} · {new Date(t.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}{t.by_name ? ` · ${t.by_name}` : ''}</div>
          <b>{usd(t.amount_cents)}</b>
        </div>))}
    </Modal>
  );
}
